// fetchDualCategory.ts

import {
  getQueryForCourses,
  getQueryForProgramTypes,
  getQueryForStreams,
} from "@/utils/collegeListingPageQuery/collegeListingQueryParams";
import { fetchListingPageDataWithPagination } from "./fetchListingPageDataWithPagination";

interface DualCategory {
  categoryType: string;
  categoryFilterValue: string;
  courseName?: string;
}

/**
 * Fetch courses & streams and determine if urlAcademicCategory matches a stream or course.
 *
 * @param type - The type of category (e.g., "dual-category")
 * @param categoryFilterValue - The string to match (e.g., "engineering", "btech", etc.)
 * @returns DualCategory object with `categoryType` ("stream", "course", or "none") and `urlAcademicCategory`.
 */
export async function fetchDualCategory(
  type: string | null,
  urlAcademicCategory: string | null
): Promise<DualCategory> {
  let dualCategory: DualCategory = {
    categoryType: "",
    categoryFilterValue: "",
  };

  if (type === "dual-category" && urlAcademicCategory !== null) {
    // 1) Fetch courses
    const courseResponse = await fetchListingPageDataWithPagination(
      JSON.stringify(getQueryForCourses),
      getQueryForCourses,
      "courses"
    );

    const coursesData = courseResponse?.results ?? [];

    // 2) Fetch streams
    const streamResponse = await fetchListingPageDataWithPagination(
      JSON.stringify(getQueryForStreams),
      getQueryForStreams,
      "streams"
    );

    const streamsData = streamResponse?.results ?? [];

    const programResponse = await fetchListingPageDataWithPagination(
      JSON.stringify(getQueryForProgramTypes),
      getQueryForProgramTypes,
      "program-types"
    );

    const programData = programResponse?.results ?? [];

    // 3) Try to match a stream first
    const foundStream = streamsData.find((stream: { stream_name: string }) => {
      const processedStreamName = stream.stream_name.replace(/-/g, " ").toLowerCase();
      return processedStreamName === urlAcademicCategory?.trim()?.replace(/-/g, " ")?.toLowerCase();
    });

    if (foundStream) {
      dualCategory.categoryType = "stream";
      dualCategory.categoryFilterValue = foundStream.stream_name;
    } else {
      const foundCourse = coursesData.find(
        (course: { course_url: string }) =>
          course.course_url.trim().toLowerCase() === urlAcademicCategory.trim().toLowerCase()
      );

      const foundProgramType = programData.find(
        (program_type: { program_type: string }) =>
          program_type.program_type.trim().toLowerCase() === urlAcademicCategory.trim().toLowerCase()
      );

      if (foundCourse) {
        dualCategory.categoryType = "course";
        dualCategory.categoryFilterValue = foundCourse.course_url;
        dualCategory.courseName = foundCourse.course_name;
      } else if (foundProgramType) {
        dualCategory.categoryType = "program_type";
        dualCategory.categoryFilterValue = foundProgramType.program_type;
      } else {
        dualCategory.categoryType = "none";
        dualCategory.categoryFilterValue = "";
        console.log("No matching stream or course found for:", urlAcademicCategory);
      }
    }
  }

  return dualCategory;
}
